import React, { useCallback, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, RefreshCw, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { RecommendationsCard } from '../../../components/RecommendationsCard/RecommendationsCard'
import {
  PerformanceDashboardCard,
  PipelineSnapshotCard,
} from '../../../components/PerformanceDashboard'
import {
  demoResetSummary,
  type ActionSummaryData,
} from '../../../services/recommendations_api'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/authentication/useAuth'
import useApiService from '../../../services/api'
import ReactSelectWrapper from '../../../components/FormUtils/ReactSelectWrapper'
import type { SingleValue } from 'react-select'
import { useHideFooter } from '../../../context/layout'
import { NewFooter } from '../../../components/Layout/Footer/NewFooter'
import { ErrorBoundary } from '../../../components/UI/ErrorBoundary'
import { cn } from '../../../components/UI/cn'

/**
 * Home page - Your daily recommendations and priorities
 *
 * Two-column layout: left column has greeting + performance dashboard,
 * right column has AI recommendations. Collapses to single column on mobile.
 *
 * Superusers can select any user to view/generate their recommendations.
 */
export function HomePage() {
  useHideFooter()
  const [isGenerating, setIsGenerating] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>()
  const [siderOpen, setSiderOpen] = useState(true)
  const queryClient = useQueryClient()
  const { user, isAdmin, isLimited } = useAuth()
  const { data: users } = useApiService().useGetUsers(
    (!user?.is_superuser && !isAdmin) || isLimited
  )

  const isSuperuser = user?.is_superuser
  const canManageSummaries = isSuperuser || isAdmin

  const summaryQueryKey = useMemo(
    () => ['recommendations', selectedUserId ?? 'self'],
    [selectedUserId]
  )

  const handleDemoReset = async () => {
    setIsGenerating(true)
    try {
      await demoResetSummary(selectedUserId)
      // Invalidate to force a fresh fetch — the reset is synchronous so
      // the DB already has the new summary.
      await queryClient.invalidateQueries({ queryKey: summaryQueryKey })
    } catch (error) {
      console.error('Failed to reset demo:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSummaryLoaded = useCallback(
    (summary: ActionSummaryData | null) => {
      if (summary?.status === 'COMPLETED' || summary?.status === 'FAILED') {
        setIsGenerating(false)
      }
    },
    []
  )

  const userOptions = useMemo(() => {
    const others =
      users?.map((u) => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.email})`,
      })) ?? []
    // Superusers are excluded from the users/ endpoint — prepend self so they
    // can select their own summary from the dropdown
    if (user && !others.find((o) => o.value === user.id)) {
      others.unshift({
        value: user.id,
        label: `${user.first_name} ${user.last_name} (${user.email}) (me)`,
      })
    }
    return others
  }, [users, user])
  const selectedUser =
    users?.find((u) => u.id === selectedUserId) ??
    (selectedUserId === user?.id ? user : undefined)
  const selectedUserOption = userOptions.find(
    (option) => option.value === selectedUserId
  )

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: white background — greeting + performance + pipeline */}
        <div className="flex-1 bg-surface overflow-auto py-4 px-6 lg:px-8">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-fg">Good morning!</h1>
              <p className="mt-1 text-lg text-fg-secondary">
                Here&apos;s what needs your attention today
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <section>
              <h2 className="text-sm font-medium text-fg-muted uppercase tracking-wide mb-3">
                Your Performance
              </h2>
              <PerformanceDashboardCard userId={selectedUserId} />
            </section>
            <PipelineSnapshotCard userId={selectedUserId} />
          </div>
        </div>

        {/* Right: grey sidebar — Action Center */}
        <div
          className={cn(
            'flex-shrink-0 bg-surface-alt border-l border-line flex flex-col overflow-hidden transition-all',
            siderOpen ? 'w-2/5 min-w-[480px]' : 'w-[70px]'
          )}
        >
          {siderOpen ? (
            <>
              <ErrorBoundary
                fallback={
                  <div className="rounded-lg border border-red-200 bg-red-50 p-6 mx-6 mt-8">
                    <p className="text-red-800 font-medium">
                      Failed to load recommendations
                    </p>
                    <p className="mt-1 text-sm text-red-600">
                      Please try refreshing the page
                    </p>
                  </div>
                }
              >
                <RecommendationsCard
                  className="px-6"
                  userId={selectedUserId}
                  onSummaryLoaded={handleSummaryLoaded}
                  headerSlot={
                    <div className="flex items-end justify-between pt-6 pb-1">
                      <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-semibold text-fg">
                          Action Center
                        </h2>
                        {isSuperuser && (
                          <button
                            type="button"
                            onClick={handleDemoReset}
                            disabled={isGenerating}
                            className="p-1 rounded text-fg-muted hover:text-fg hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            title={
                              selectedUserId
                                ? `Reset demo for ${selectedUser?.email}`
                                : 'Reset demo'
                            }
                          >
                            <RefreshCw
                              size={14}
                              className={isGenerating ? 'animate-spin' : ''}
                            />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {canManageSummaries && users && (
                          <ReactSelectWrapper
                            className="min-w-[200px] text-sm"
                            options={userOptions}
                            value={selectedUserOption ?? null}
                            onChange={(option: unknown) => {
                              const typedOption = option as SingleValue<{
                                label: string
                                value: number
                              }>
                              const id = typedOption?.value
                              setSelectedUserId(
                                id === user?.id ? undefined : id
                              )
                              setIsGenerating(false)
                            }}
                            placeholder="My Summary"
                            isClearable
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => setSiderOpen(false)}
                          className="w-[38px] h-[38px] flex items-center justify-center rounded-[6px] border border-line text-fg-muted hover:text-fg hover:border-brand transition-colors"
                          title="Collapse Action Center"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  }
                />
              </ErrorBoundary>
              <div className="flex-shrink-0 bg-surface border-t border-line px-6 py-4">
                <Link
                  to="/settings/ai"
                  className="flex items-center justify-center gap-1.5 w-full py-2 text-sm text-fb-dark-green font-medium rounded-lg border border-line hover:bg-surface-alt transition-colors"
                >
                  <Sparkles size={16} />
                  Manage AI Agents
                </Link>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-3 pt-6 w-full">
              <button
                type="button"
                onClick={() => setSiderOpen(true)}
                className="w-[38px] h-[38px] flex items-center justify-center rounded-[6px] border border-line text-fg-muted hover:text-fg hover:border-brand transition-colors"
                title="Expand Action Center"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex items-center gap-2 text-base font-medium text-fb-dark-green tracking-wide [writing-mode:vertical-rl] rotate-180">
                <Sparkles size={16} />
                Action Center
              </div>
            </div>
          )}
        </div>
      </div>
      <NewFooter />
    </div>
  )
}
